<template>
  <!-- Loading State -->
  <div v-if="loading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
    </div>
  </div>

  <!-- Error State -->
  <PageError 
    v-else-if="error"
    :error="error"
    :errorCode="errorCode" 
  />

  <div v-else-if="blackbox" class="p-6  dark:text-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Endpoint Availability
        </button>
        Endpoint Availability Details: {{ blackbox.job_name }}
        <div class="relative group">
          <button
            type="button"
            class="hover:bg-transparent bg-transparent ml-2"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
    </div>
    <!-- Table Header -->
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
          <tr>
            <th class="py-2 px-4 w-1/3">Field</th>
            <th class="py-2 px-4 w-2/3">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(field, index) in visibleFields"
            :key="field.key"
            :id="'blackbox-field-' + field.key"
            :class="['border-b dark:border-gray-700', index % 2 === 0 ? 'hover:bg-gray-900/30 transition-colors' : 'bhover:bg-gray-900/30 transition-colors']"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              <template v-if="editingField === field.key">
                <div class="flex items-start gap-2 w-full">

                  <template v-if="field.key!=='probe' && field.key!=='targets' && field.key!=='module' && field.key!=='site'">
                    <div class="flex-grow">
                      <input 
                        v-model="editValue"
                        :type="field.type"
                        @focus=clearFieldError(field.key)
                        :class="[
                            'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                            fieldErrors[field.key]
                              ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          ]"
                      />
                      <FieldError v-if="fieldErrors[field.key]" :message="fieldErrors[field.key]" class="mt-1"/>
                    </div>
                  </template>

                  <GlassSelect
                    v-else-if="field.key == 'probe'"
                    v-model="editValue"
                    :options="probes"
                    placeholder="Select a probe"
                  />


                  <GlassSelect
                    v-else-if="field.key == 'site'"
                    v-model="editValue"
                    :options="sites"
                    placeholder="Select a site"
                    :error="!!fieldErrors[field.key]"
                  />

                  <!-- Targets Pop Up -->
                  <Teleport v-else-if="field.key === 'targets'" to="body">
                    <div class="flex fixed inset-0 items-center justify-center bg-black/50 backdrop-blur-sm z-40">
                      <div class="glass-card p-6 mb-2 w-[80%] min-h-[18%]">
                        <label>Endpoint Availability Targets</label>
                        <input
                          v-model="editValue"
                          :class="['my-2 w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                            ? 'ring-2 ring-red-500  focus:ring-red-500'
                            : 'focus:ring-blue-500'
                          ]"
                        >
                        <FieldError :message="fieldErrors[field.key]"/>
                        <div class="flex justify-between">
                          <button
                            v-if="addingTarget"
                            type="button"
                            :class="{
                              'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-700 focus:ring-gray-200 ': editValue === null,
                              'bg-green-600 hover:bg-green-700 focus:ring-green-500 ': editValue !== null,
                              'btn text-white focus:outline-none focus:ring-2 transition duration-200': true
                            }"
                            @click="addTarget(editValue)"
                          >
                            Add Target
                          </button>
                          <button
                            v-else-if="!lastTarget"
                            type="button"
                            class="btn bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            @click="removeOldTargets(editValue)"
                          >
                            Delete Target
                          </button>
                          <div class="space-x-1">
                            <button
                              v-if="canChangeBlackbox || user?.is_staff"
                              type="button"
                              class="btn bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                              @click="saveEdit(field.key)"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              class="btn bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                              @click="cancelEdit(field.key)"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        <div class="flex flex-wrap gap-2 mt-2">
                          <div
                            v-for="target in targets"
                            :key="target"
                            class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            <span
                              v-show="targetAdded"
                              class="text-sm font-medium text-gray-700 dark:text-gray-200"
                            >
                              {{ target }}
                            </span>
                            <button
                              class="text-gray-500 hover:text-gray-700 dark:text-gray-100 focus:outline-none"
                              @click="removeTarget(target)"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fill-rule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clip-rule="evenodd"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Teleport>
                  

                  <GlassSelect
                    v-else-if="field.type === 'select'"
                    :id="field.key"
                    v-model="editValue"
                    :options="field.options"
                    placeholder="Select an option"
                  />

                  <!-- General Save and Edit Buttons -->
                  <div class="flex gap-2 flex-shrink-0">
                    <button
                      @click="saveEdit(field.key)"
                      class="ml-2 bg-green-600 hover:bg-green-700 text-white btn"
                    >
                      Save
                    </button>
                    <button
                      @click="cancelEdit(field.key)"
                      class="ml-2 bg-red-600 hover:bg-red-700 text-white btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </template>

              
              <template v-else>
                <!-- Display Field Data -->
                <div class="flex">
                  <div v-if="field.key === 'targets'" class="w-[50%] relative">
                    <GlassSelect
                      :model-value="''"
                      :options="formatValue(field.key, blackbox[field.key]) || []"
                      placeholder="Manage Targets"
                      @change="(target) => target && editTarget(field.key, target)"
                    />
                </div>
                <span v-else class="flex-grow">{{ formatValue(field.key, blackbox[field.key]) }}</span>
      
                <!-- Handle Editing -->
                <div class="relative w-1/2">
                  <button v-if="handleNoEdit(field.key) && field.key == 'targets'"
                    @click="startEdit(field.key)"
                    @mouseover="showToolTip=true, hoveritem=field.key"
                    @mouseleave="showToolTip=false"
                    :id=field.key
                    class="ml-2 p-1 ml-10 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                    >
                    <svg xmlns="http://www.w3.org/2000/svg" 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      stroke-width="2" 
                      stroke-linecap="round" 
                      stroke-linejoin="round" 
                      class="lucide lucide-copy-plus">
                      <line x1="15" x2="15" y1="12" y2="18"/>
                      <line x1="12" x2="18" y1="15" y2="15"/>
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                    </svg>
                    </button>
                    <button 
                      v-if="handleNoEdit(field.key) && field.key !== 'targets'"
                      @click="startEdit(field.key)"
                      @mouseover="showToolTip=true, hoveritem=field.key"
                      @mouseleave="showToolTip=false"
                      :id=field.key
                      class="ml-2 p-1 ml-10 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                    >
                      <svg
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                    <span 
                      v-if="showToolTip && hoveritem === field.key"
                      class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-1/2 rounded py-1 px-2  dark:shadow-lg dark:bg-gray-500 bg-gray-800 bg-gray-800 text-white text-center "
                        >
                        {{ field.key == 'targets' ? 'Add '  : 'Change ' }}{{field.label}}
                    </span>
                </div>
                </div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getBlackboxById, updateBlackbox } from "@/services/blackboxService";
import { getAllProbes } from "@/services/probeService";
import { getSites } from "@/services/siteService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import { formatError } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import ExplainConfigButton from "@/components/ExplainConfigButton.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  components : {
    FieldError,
    PageError,
    ExplainConfigButton,
    GlassSelect
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      fieldErrors: {},
      blackbox: null,
      showToolTip: false,
      editingField: null,
      editValue: null,
      targets : [],
      targetAdded : false,
      addingTarget: false,
      lastTarget: false,
      selectedTarget: null,
      deletingTarget : false,
      probes: [],
      sites: [],
      noEditFields: ['job_name', 'last_modified', 'created_date'],
      displayFields: [
        { key: "job_name", label: "Job Name", type: "text" },
        { key: "module", label: "Module", type: "select",  options: ["http_2xx", "icmp", "udp", "tcp_connect"] },
        { key: "test_descriptor", label: "Test Descriptor", type: "text" },
        { key: "targets", label: "Targets", type: "text" },
        { key: "scrape_interval", label: "Scrape Interval", type: "number", staffOnly: true },
        { key: "scrape_timeout", label: "Scrape Timeout", type: "number", staffOnly: true },
        { key: "last_modified", label: "Last Modified", type: "text" },
        { key: "created_date", label: "Created Date", type: "text" },
        { key: "probe", label: "Probe", type: "number" },
        { key: "site", label: "Site", type: "number" }
      ],
      canChangeBlackbox: true,
      blackboxDetailSteps: [
        {
          element: '#blackbox-field-job_name',
          action: 'display',
          message: 'The job name identifies this endpoint availability check.'
        },
        {
          element: '#blackbox-field-module',
          action: 'display',
          message: 'The module determines the check type (HTTP, ICMP, TCP, etc.). Click the pencil icon to change.'
        },
        {
          element: '#blackbox-field-targets',
          action: 'display',
          message: 'The targets being monitored. Click the icon to add or manage targets.'
        },
        {
          element: '#blackbox-field-scrape_interval',
          action: 'display',
          message: 'How often (in seconds) the targets are checked. Click the pencil icon to edit.'
        },
        {
          element: '#blackbox-field-probe',
          action: 'display',
          message: 'The probe assigned to this check. Click the pencil icon to change.'
        },
      ],
      error: "",
      errorCode: "",
      pendingTarget: null,
      loading: false
    };
  },
  computed: {
    user(){
      return useStore.getUser();
    },
    visibleFields() {
      return this.displayFields.filter(f => !f.staffOnly || useStore.isStaff());
    }
  },
  async beforeMount(){
    try {
      const validation = this.tenant.validateSchemaForOperation();
      this.probes = await getAllProbes(validation.schemaName);
      this.sites = await getSites(validation.schemaName);
    } catch (error) {
      console.error("Error fetching probes/sites:", error);
    }
  },
  async mounted() {
    const blackboxId = this.$route.params.id;
    try {
      this.loading = true;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to get blackbox details!";
        this.errorCode = "422!";
        return;
      }
      this.probes = await getAllProbes(validation.schemaName);
      this.blackbox = await getBlackboxById(blackboxId, validation?.schemaName);

      if (this.user) {
        this.canChangeBlackbox = await useStore.hasPermission("Can change Blackbox");
      }
    } catch (error) {
      console.error("Error fetching blackbox:", error);
      const { code, message } = formatError(error);
      this.errorCode = code;
      this.error = message;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async startTutorial() {
      await this._start(this.blackboxDetailSteps);
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    startEdit(key) {
      this.editingField = key;
      if (key !== 'targets') {
        this.editValue = this.blackbox[key];
        this.originalValue = this.editValue;
      } else {
        this.addingTarget = true;
      }
      this.showToolTip = false;
    },
    editTarget(key,target) {
      this.editingField = key;
      this.selectedTarget = this.blackbox.targets.find(item => item === target);
      if (this.blackbox.targets.length === 1) this.lastTarget = true;
      this.editValue = this.selectedTarget;
      this.showToolTip = false;
    },
    cancelEdit(key) {
      this.clearFieldError(key);
      this.editingField = null;
      this.editValue = null;
      this.targets = [];
      this.addingTarget = false;
      this.fieldErrors = {};
      if (key !== 'targets') this.blackbox[key] = this.originalValue;
    },
    goBack(){
      this.$router.push({
        name: 'Blackboxes',
        query: this.tenant.addSchemaParam()
      });
    },
    async saveEdit(key) {
      let originalProbe;
      let originalSite;
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to save blackbox changes!";
          this.errorCode = "422!";
          return;
        }

        this.fieldErrors = {};

        // original values for rollback
        originalProbe = this.blackbox.probe;
        originalSite = this.blackbox.site;

        // Validate probe/site exclusivity
        if (key === 'probe' && this.editValue) {
          this.blackbox.site = null;
        }
        if (key === 'site' && this.editValue) {
          this.blackbox.probe = null;
        } 

        if (key === 'targets'){
          this.handleTarget();
        };

        if (Object.keys(this.fieldErrors).length > 0){
          this.editValue = this.pendingTarget;
          this.blackbox.probe = originalProbe;
          this.blackbox.site = originalSite;
          return;
        } 

        this.blackbox[key] = this.editValue;
        this.editValue = null;
        const updatedBlackbox = await updateBlackbox(
          this.blackbox.id,
          this.blackbox,
          validation.schemaName
        );
        this.blackbox = { ...this.blackbox, ...updatedBlackbox };
        this.editingField = null;
        this.editValue = null;
        this.targets = [];
        this.addingTarget = false;
        this.deletingTarget = false;

      } catch (error) {
        this.blackbox.probe = originalProbe;
        this.blackbox.site = originalSite;
        this.fieldErrors = {}
        const items = this.displayFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });
        console.error("Error updating blackbox:", error);
      }
    },
    handleTarget(){
      const isUpdating = !this.deletingTarget && !this.addingTarget;

      if (isUpdating) {

          this.fieldErrors = {};
          const index = this.blackbox.targets.indexOf(this.selectedTarget);
          this.pendingTarget = this.editValue;

          this.targetValidation(this.pendingTarget);
          if (Object.keys(this.fieldErrors).length > 0) return;

          this.blackbox.targets[index] = this.editValue;
          this.editValue = this.blackbox.targets; 
      } 
      else{
          this.editValue = this.targets.concat(this.blackbox.targets);
      }
    },
    formatValue(key, value) {  
      if (key === 'probe'){
        if (value === null || value === undefined) return "N/A"; 
        const displayValue = this.probes.find(item => item.id === value)
        return displayValue? displayValue["name"] : "redacted"
      }
      if(key === 'targets'){
        console.log(key, value)
      }
      if (key === 'site'){
        if (value === null || value === undefined) return "N/A"; 
        const displayValue = this.sites.find(item => item.id === value)
        return displayValue? displayValue["name"] : "redacted"
      }
      
      return value !== null && value !== undefined ? value : "N/A";
    },
    handleNoEdit(key) {
      if (this.canChangeBlackbox || this.user?.is_staff){
        return !this.noEditFields.includes(key);
      }
    },
    addTarget(target){
      if (target !== null){
        this.fieldErrors = {};
        this.targetValidation(target)
        if (Object.keys(this.fieldErrors).length > 0) return;
        this.targets.push(target);
        this.targetAdded = true;
        this.editValue = null;
      }
    },
    targetValidation(target){
      this.fieldErrors = {}
      if (this.blackbox.module === "tcp_connect"){
        const portMatch = target.match(/:(\d+)$/);
        if (!portMatch) {
          this.fieldErrors.targets = 'Target must include a port (e.g., google.com:443 or 9.9.9.9:443)';
          return;
        }
        
        const port = parseInt(portMatch[1]);
        if (port < 1 || port > 65535) {
          this.fieldErrors.targets = 'Port must be between 1 and 65535';
        }

        if (target.startsWith('http://') || target.startsWith('https://')){
          this.fieldErrors.targets = 'Target should not include http:// or https://';
        }

      } else if (this.blackbox.module === "http_2xx"){

        if (!target.startsWith('http://') && !target.startsWith('https://')) {
           this.fieldErrors.targets = "Please add http:// or https:// (for example: https://example.com)";
           return;
        }
      }
    },
    removeTarget(target){
      const index = this.targets.indexOf(target);
      this.targets.splice(index,1);
      this.blackbox.targets.splice(index,1);
      this.fieldErrors = {};
    },
    removeOldTargets(target){
      const index = this.blackbox.targets.indexOf(target);
      this.blackbox.targets.splice(index,1); 
      this.editValue = null;
      this.deletingTarget = true;
    }
  },
};
</script>
